import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Lock, ShieldCheck, Truck, RefreshCw, ChevronLeft } from "lucide-react";
import { toast } from "sonner";

import { useCart } from "../../context/CartContext";
import { useTemplate } from "../../context/TemplateContext";
import { createOrder } from "../../lib/api";

const INITIAL = {
  email: "", full_name: "", address_line1: "", address_line2: "",
  city: "", state: "", postal_code: "", country: "United States",
  phone: "", notes: "",
};

/**
 * SRC-styled checkout. Two-column desktop layout:
 *   Left:  shipping form (email → address → payment stub)
 *   Right: sticky order summary (line items, subtotal, shipping, total)
 *
 * Real Stripe wiring is behind `checkoutEnabled` from TemplateContext; when
 * disabled we localStorage the cart and post a demo order (matches the
 * existing app's checkout flow).
 */
export default function Checkout() {
  const cart = useCart();
  const { items, subtotal, shipping, total, clear, lineKey } = cart;
  const { basePath, slug, checkoutEnabled } = useTemplate();
  const [form, setForm] = useState(INITIAL);
  const [placing, setPlacing] = useState(false);
  const navigate = useNavigate();

  if (items.length === 0) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-32 text-center" data-testid="checkout-empty">
        <h1 className="font-display text-4xl">Your cart is empty.</h1>
        <p className="text-neutral-500 mt-2">Add a piece before checking out.</p>
        <Link to={`${basePath}/shop`} className="inline-block mt-8 bg-black text-white px-8 py-4 text-xs uppercase tracking-widest">
          Browse the shop
        </Link>
      </div>
    );
  }

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setPlacing(true);
    try {
      const order = await createOrder({
        ...form,
        slug,
        checkout_enabled: !!checkoutEnabled,
        items: items.map((it) => ({
          product_id: it.product_id,
          slug: it.slug,
          quantity: it.quantity,
          price: it.price,
          size: it.variant?.size,
          color: it.variant?.color,
          variant: it.variant || {},
        })),
      });
      const orderNumber = order.order_number || order.id;
      clear();
      navigate(`${basePath}/order/${orderNumber}`);
    } catch (err) {
      toast.error("Could not place order. Please try again.");
    } finally {
      setPlacing(false);
    }
  };

  return (
    <div data-testid="checkout-page" className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-16 sm:py-24">
      <Link to={`${basePath}/shop`} className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] mb-8 link-underline pb-0.5">
        <ChevronLeft className="w-4 h-4" strokeWidth={1.5} /> Continue shopping
      </Link>
      <h1 className="font-display text-5xl sm:text-6xl leading-none mb-12">Checkout.</h1>

      <div className="grid lg:grid-cols-[1.4fr,1fr] gap-12">
        <form onSubmit={onSubmit} className="space-y-10">
          {/* Contact */}
          <section>
            <h2 className="text-xs uppercase tracking-[0.2em] mb-4 font-semibold">1 · Contact</h2>
            <input
              type="email" required name="email" value={form.email} onChange={onChange}
              placeholder="Email"
              data-testid="checkout-email"
              className="w-full border border-neutral-300 px-4 py-3 text-sm focus:outline-none focus:border-black transition-colors"
            />
          </section>

          {/* Shipping */}
          <section>
            <h2 className="text-xs uppercase tracking-[0.2em] mb-4 font-semibold">2 · Shipping</h2>
            <div className="grid gap-3">
              <input required name="full_name" value={form.full_name} onChange={onChange} placeholder="Full name" data-testid="checkout-name" className="w-full border border-neutral-300 px-4 py-3 text-sm focus:outline-none focus:border-black" />
              <input required name="address_line1" value={form.address_line1} onChange={onChange} placeholder="Address" data-testid="checkout-address1" className="w-full border border-neutral-300 px-4 py-3 text-sm focus:outline-none focus:border-black" />
              <input name="address_line2" value={form.address_line2} onChange={onChange} placeholder="Apt, suite (optional)" className="w-full border border-neutral-300 px-4 py-3 text-sm focus:outline-none focus:border-black" />
              <div className="grid grid-cols-2 gap-3">
                <input required name="city" value={form.city} onChange={onChange} placeholder="City" data-testid="checkout-city" className="border border-neutral-300 px-4 py-3 text-sm focus:outline-none focus:border-black" />
                <input required name="state" value={form.state} onChange={onChange} placeholder="State / Province" data-testid="checkout-state" className="border border-neutral-300 px-4 py-3 text-sm focus:outline-none focus:border-black" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <input required name="postal_code" value={form.postal_code} onChange={onChange} placeholder="Postal code" data-testid="checkout-zip" className="border border-neutral-300 px-4 py-3 text-sm focus:outline-none focus:border-black" />
                <input required name="country" value={form.country} onChange={onChange} placeholder="Country" data-testid="checkout-country" className="border border-neutral-300 px-4 py-3 text-sm focus:outline-none focus:border-black" />
              </div>
              <input name="phone" value={form.phone} onChange={onChange} placeholder="Phone (optional)" className="w-full border border-neutral-300 px-4 py-3 text-sm focus:outline-none focus:border-black" />
            </div>
          </section>

          {/* Payment stub */}
          <section>
            <h2 className="text-xs uppercase tracking-[0.2em] mb-4 font-semibold">3 · Payment</h2>
            <div className="border border-neutral-300 p-4 text-sm text-neutral-600 flex items-center gap-2">
              <Lock className="w-4 h-4" strokeWidth={1.5} />
              {checkoutEnabled ? "You'll be redirected to secure checkout." : "Payment simulated in demo mode."}
            </div>
          </section>

          <button
            type="submit" disabled={placing}
            data-testid="place-order-btn"
            className="w-full bg-black text-white hover:bg-neutral-800 py-5 text-xs uppercase tracking-[0.2em] disabled:opacity-50 transition-colors"
          >
            {placing ? "Placing order..." : `Place order · $${total.toFixed(2)}`}
          </button>

          <ul className="grid grid-cols-3 gap-3 text-[11px] text-neutral-500">
            <li className="flex items-center gap-1"><Truck className="w-3 h-3" strokeWidth={1.5} /> Fast shipping</li>
            <li className="flex items-center gap-1"><RefreshCw className="w-3 h-3" strokeWidth={1.5} /> Free returns</li>
            <li className="flex items-center gap-1"><ShieldCheck className="w-3 h-3" strokeWidth={1.5} /> Secure checkout</li>
          </ul>
        </form>

        {/* Order summary */}
        <aside className="border border-neutral-200 p-6 lg:sticky lg:top-32 lg:self-start">
          <p className="text-xs uppercase tracking-[0.2em] mb-4">Order summary</p>
          <ul className="divide-y divide-neutral-200">
            {items.map((it) => (
              <li key={lineKey(it)} className="py-4 flex gap-4">
                {it.image && <img src={it.image} alt={it.name} className="w-16 h-20 object-cover flex-shrink-0" />}
                <div className="flex-1 min-w-0">
                  <p className="font-heading text-sm truncate">{it.name}</p>
                  <p className="text-xs text-neutral-500 mt-0.5">
                    {[it.variant?.size, it.variant?.color].filter(Boolean).join(" · ")}
                  </p>
                  <p className="text-xs text-neutral-500 mt-0.5">Qty {it.quantity}</p>
                </div>
                <p className="font-heading text-sm">${(it.price * it.quantity).toFixed(2)}</p>
              </li>
            ))}
          </ul>
          <div className="mt-6 space-y-2 text-sm">
            <div className="flex justify-between"><span>Subtotal</span><span data-testid="summary-subtotal">${subtotal.toFixed(2)}</span></div>
            <div className="flex justify-between text-neutral-500"><span>Shipping</span><span>{shipping === 0 ? "Free" : `$${shipping.toFixed(2)}`}</span></div>
            <div className="border-t border-neutral-200 pt-3 flex justify-between font-heading text-base">
              <span>Total</span><span data-testid="summary-total">${total.toFixed(2)}</span>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
